import { createInstance } from 'i18next';
import Backend from 'i18next-chained-backend';
import HttpApi from 'i18next-http-backend';
import LocalStorageBackend from 'i18next-localstorage-backend';
import resourcesToBackend from 'i18next-resources-to-backend';
import { Config } from 'next-i18n-router/dist/types';
import { initReactI18next } from 'react-i18next/initReactI18next';

import { languages, Locale } from './languages';
import { LANGUAGE_PARAM } from './variables';

export const availableLng = Object.keys(languages).reduce((acc, keyLoc) => {
  return acc.concat(languages[keyLoc].map((locale) => locale.locale));
}, [] as string[]);

export const i18nConfig: Config = {
  locales: availableLng,
  defaultLocale: Locale.ENGLISH,
  localeCookie: LANGUAGE_PARAM,
  localeDetector: () => Locale.ENGLISH,
};

export const namespaces = [
  'common',
  'validation',
  'deposits',
  'bonuses',
  'jackpot',
  'loyalty',
  'lpAssets',
  'mobileApp',
];

export const DEFAULT_NS = namespaces[0];

export async function initTranslations(locale: string, ns?: string | string[]) {
  const i18nInstance = createInstance();

  await i18nInstance
    .use(initReactI18next)
    .use(Backend)
    .init({
      load: 'currentOnly',
      lng: locale,
      fallbackLng: i18nConfig.defaultLocale,
      supportedLngs: i18nConfig.locales,
      defaultNS: DEFAULT_NS,
      fallbackNS: DEFAULT_NS,
      ns: ns ?? namespaces,
      preload: typeof window === 'undefined' ? i18nConfig.locales : [],
      interpolation: { escapeValue: false },
      detection: {
        order: ['cookie'],
        caches: ['cookie'],
      },
      backend: {
        backends: [
          LocalStorageBackend, // primary
          HttpApi, // 1st fallback
          resourcesToBackend(
            // 2nd fallback
            (language: string, namespace: string) =>
              import(`/locales/${language}/${namespace}.json`)
          ),
        ],
        backendOptions: [
          { projectId: 'levelup' },
          {
            loadPath: `https://api.i18nexus.com/project_resources/translations/{{lng}}/{{ns}}.json?api_key=${process.env.NEXT_PUBLIC_I18NEXUS_API_KEY}`,
          },
        ],
        cacheHitMode: 'refreshAndUpdateStore',
        refreshExpirationTime: Number(
          process.env.NEXT_PUBLIC_I18NEXUS_REFRESH_TIME || 60000
        ),
      },
    });

  return i18nInstance;
}

export async function getServerTranslation(
  locale: string,
  ns: string | string[] = 'common'
) {
  const i18nextInstance = await initTranslations(locale, ns);

  return {
    t: i18nextInstance.getFixedT(locale, Array.isArray(ns) ? ns[0] : ns),
    i18n: i18nextInstance,
  };
}

export const getLangAttr = (locale: string) => {
  if (!locale) return Locale.ENGLISH;
  if (locale === Locale.NORWEGIAN) return 'en-NO';

  return locale;
};

export type TranslationsState = {
  locale: Locale;
  lang: string;
};

export const translationsState: TranslationsState = {
  locale: i18nConfig.defaultLocale as Locale,
  lang: getLangAttr(i18nConfig.defaultLocale),
};

export const hasTranslation = (t: (key: string) => string, key: string) =>
  t(key) !== key;
